import { ChangeDetectorRef, Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { DeviceService } from '@app/core/services/device.service';
import { Device, DeviceType } from '@app/core/models/device';
import { ResponsiveDropdownScaffoldComponent } from '../dropdown/responsive-dropdown/responsive-dropdown-scaffold.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {MessageBoxIcon, ModalButton, ModalButtons} from '@app/core/models/modal-ref';
import {ModalService} from '@app/core/services/modal.service';
import {Router} from '@angular/router';

type DeviceDropdownValue = { device: Device, username?: string };

@Component({
  selector: 'app-device-dropdown',
  templateUrl: './device-dropdown.component.html',
  styleUrls: ['./device-dropdown.component.scss']
})
export class DeviceDropdownComponent implements OnInit, OnDestroy {
  public DeviceType = DeviceType;

  private readonly onDestroy = new Subject<boolean>();

  /**
   * Contains dropdown item values in a form so that they can be processed in the template
   * (each outer array represents a bunch of items. If group length > 1, the items are the device users)
   */
  public dropdownItems: DeviceDropdownValue[][] = [];
  public selectedDevice: DeviceDropdownValue;

  @ViewChild('dropdownScaffold')
  public dropdownScaffold: ResponsiveDropdownScaffoldComponent;

  constructor(public deviceService: DeviceService,
              private router: Router,
              private modal: ModalService,
              private cdr: ChangeDetectorRef) { }

  public ngOnInit(): void {
    // Update selectedDevice on every external device switch
    this.deviceService.onDeviceSwitch
      .pipe(takeUntil(this.onDestroy))
      .subscribe((newDevice) => {
        // We assume that the device list is completely loaded if the initial device switch happens
        // Updating the dropdown items on every device switch is unnecessary because we should do it on every device array change
        // But we keep it for now (the initial generation of the dropdownItems also takes place here)
        this.generateDropdownItems();

        // Find the active device in our dropdown item values
        let match: DeviceDropdownValue;

        for (const group of this.dropdownItems) {
          match = group.find(item => {
            return item.device === newDevice.device
              && ((newDevice.username == null && item.username == null) || item.username === newDevice.username);
          });

          if (match) {
            break;
          }
        }

        if (this.selectedDevice !== match) {
          this.selectedDevice = match;
        }
      });
  }

  public ngOnDestroy() {
    this.onDestroy.next(true);
    this.onDestroy.complete();
  }

  public get isNoDevicesMode(): boolean {
    return this.deviceService.isInitialized && this.deviceService.devices.length === 0;
  }

  public get isOverviewMode(): boolean {
    return this.router.url.startsWith('/dashboard/overview');
  }

  public onAllDevicesClick() {
    this.dropdownScaffold?.hide();
    this.router.navigate(['/dashboard/overview']);
  }

  /**
   * Returns text that is shown in the dropdown as the currently selected device
   */
  public getSelectedItemText() {
    let text = '';

    if (this.selectedDevice?.username != null) {
      text += `${this.selectedDevice.username} – `;
    }

    text += this.selectedDevice?.device.name ?? '';

    return text;
  }

  public onSelectionChange() {
    // Defektes Gerät (deviceType=90): nicht switchen, stattdessen Fehlermodal zeigen und Auswahl zurücksetzen
    if (this.selectedDevice?.device.type === DeviceType.Invalid) {
      this.modal.showMessageBox({
        title: 'COMMON.DEVICE_INVALID_TITLE',
        text: 'COMMON.DEVICE_INVALID_TEXT',
        icon: MessageBoxIcon.Warning,
        buttons: ModalButtons.Ok,
        defaultButton: ModalButton.Ok
      });
      this.revertSelectionToActiveDevice();
      return;
    }

    // Only switch device if necessary
    if (this.deviceService.activeDevice.device !== this.selectedDevice.device
      || (this.selectedDevice.username != null && this.deviceService.activeDevice.username !== this.selectedDevice.username)
    ) {
      this.deviceService.switchDevice(this.selectedDevice.device, this.selectedDevice.username);

      if (this.selectedDevice.device.id === 'UNKNOWN' && this.selectedDevice.device.name === 'UNKNOWN') {
        this.router.navigate(['/licence']);
      } else if (this.isOverviewMode) {
        this.router.navigate(['/dashboard']);
      }
    }
  }

  private revertSelectionToActiveDevice() {
    const active = this.deviceService.activeDevice;
    if (!active) {
      this.selectedDevice = null;
      this.cdr.detectChanges();
      return;
    }

    for (const group of this.dropdownItems) {
      const match = group.find(item =>
        item.device === active.device
        && ((active.username == null && item.username == null) || item.username === active.username)
      );
      if (match) {
        this.selectedDevice = match;
        break;
      }
    }
    this.cdr.detectChanges();
  }

  private generateDropdownItems() {
    this.dropdownItems = [];

    for (const device of this.deviceService.devices) {
      const group = [];
      this.dropdownItems.push(group);

      if (device.users) {
        for (const user of device.users) {
          const item: DeviceDropdownValue = {
            device,
            username: user
          };
          group.push(item);
        }
      } else {
        const item: DeviceDropdownValue = { device };
        group.push(item);
      }
    }
  }

}
